<template>
  <div class="space-y-3">
    <!-- Summary bar -->
    <div class="flex items-center justify-between">
      <span class="text-navy-600 text-sm font-medium">
        {{ selectedIds.length }} of {{ documents.length }} selected
      </span>
      <div class="flex gap-1 text-xs">
        <button
          class="cursor-pointer font-medium text-green-600 hover:text-green-700"
          @click="$emit('selectAll')"
        >
          Select all
        </button>
        <span class="text-neutral-300">&middot;</span>
        <button
          class="text-navy-400 hover:text-navy-600 cursor-pointer font-medium"
          @click="$emit('deselectAll')"
        >
          Deselect all
        </button>
      </div>
    </div>

    <!-- Search input -->
    <div class="relative">
      <i
        class="pi pi-search text-navy-300 absolute top-1/2 left-3 -translate-y-1/2 text-sm"
      />
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Filter documents..."
        class="text-navy-800 placeholder:text-navy-300 w-full rounded-md border border-neutral-300 py-2 pr-3 pl-10 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none"
      />
    </div>

    <!-- Tree -->
    <div class="space-y-0.5">
      <DocumentTreeFolder
        v-for="folder in displayTree"
        :key="folder.path"
        :folder="folder"
        :selected-ids="selectedIds"
        :expanded-folders="effectiveExpandedFolders"
        :depth="0"
        @toggle-document="$emit('toggleDocument', $event)"
        @toggle-folder="$emit('toggleFolder', $event)"
        @toggle-expand="toggleExpand"
      />
    </div>

    <!-- Empty state for search -->
    <div
      v-if="filteredDocuments.length === 0 && searchQuery.trim()"
      class="text-navy-400 py-6 text-center text-sm"
    >
      No documents match &ldquo;{{ searchQuery }}&rdquo;
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"

import type { FetchedDocument } from "../../pages/Order/types"
import type { TreeFolder } from "./DocumentTreeFolder.vue"
import DocumentTreeFolder from "./DocumentTreeFolder.vue"

const props = defineProps<{
  documents: FetchedDocument[]
  selectedIds: string[]
}>()

defineEmits<{
  toggleDocument: [documentId: string]
  toggleFolder: [folderPath: string]
  selectAll: []
  deselectAll: []
}>()

const searchQuery = ref("")
const expandedFolders = ref<Set<string>>(new Set())

const filteredDocuments = computed(() => {
  if (!searchQuery.value.trim()) return props.documents
  const q = searchQuery.value.toLowerCase()
  return props.documents.filter((d) => d.file_name.toLowerCase().includes(q))
})

// Normalize Windows-style backslash paths and strip leading slashes
const normalizePath = (p: string): string =>
  p.replace(/\\/g, "/").replace(/^\/+/, "")

const buildTree = (docs: FetchedDocument[]): TreeFolder[] => {
  const root: TreeFolder = { name: "", path: "", children: [], documents: [] }

  for (const doc of docs) {
    const allParts = normalizePath(doc.folder_path).split("/").filter(Boolean)
    // folder_path includes the filename as the last segment — drop it
    const parts = allParts.slice(0, -1)
    let current = root
    let pathSoFar = ""

    for (const part of parts) {
      pathSoFar = pathSoFar ? `${pathSoFar}/${part}` : part
      let child = current.children.find((c) => c.name === part)
      if (!child) {
        child = { name: part, path: pathSoFar, children: [], documents: [] }
        current.children.push(child)
      }
      current = child
    }
    current.documents.push(doc)
  }

  return root.children
}

const fullTree = computed(() => buildTree(props.documents))
const filteredTree = computed(() => buildTree(filteredDocuments.value))

const displayTree = computed(() =>
  searchQuery.value.trim() ? filteredTree.value : fullTree.value,
)

// Collect all folder paths from a tree
const collectPaths = (folders: TreeFolder[]): string[] => {
  const paths: string[] = []
  for (const f of folders) {
    paths.push(f.path)
    paths.push(...collectPaths(f.children))
  }
  return paths
}

// Expand all folders on initial load
watch(
  () => props.documents,
  () => {
    expandedFolders.value = new Set(collectPaths(fullTree.value))
  },
  { immediate: true },
)

// When searching, auto-expand all matching folders
const effectiveExpandedFolders = computed(() => {
  if (searchQuery.value.trim()) {
    return new Set(collectPaths(filteredTree.value))
  }
  return expandedFolders.value
})

const toggleExpand = (folderPath: string) => {
  const set = expandedFolders.value
  if (set.has(folderPath)) {
    set.delete(folderPath)
  } else {
    set.add(folderPath)
  }
}
</script>
